/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import axios from 'axios'
import { v4 as uuidv4 } from 'uuid'

import { acquireTokenSilent } from '../utils/auth'
import Config from '../utils/config'
import { getCurrentTenantId } from '../utils/tenants'
import { FetchError } from './fetchError'

export async function getWithBaseUrlAndToken(url: string, fallback = false): Promise<any> {
  const token = await acquireTokenSilent()
  const fullUrl = await generateFullUrl(url)
  const correlationId = uuidv4()

  const headers = { Authorization: `Bearer ${token}`, 'X-Correlation-ID': correlationId }

  const response = await fetch(fullUrl, { headers: headers })

  if (!response.ok) {
    if (response.status === 404 && fallback) return {}
    const error = new FetchError(
      'An error occurred while performing the request. Correlation ID: ' + correlationId,
    )
    error.status = response.status
    error.info = await response.json()
    error.correlationId = correlationId

    throw error
  }

  return {
    body: await response.json(),
    etag: response.headers.get('ETag') || undefined,
  }
}

export async function getWithBaseUrlAndTokenRedirected(
  url: string,
  fallback = false,
): Promise<any> {
  const token = await acquireTokenSilent()
  const fullUrl = await generateFullUrl(url)
  const correlationId = uuidv4()

  const headers = {
    Authorization: `Bearer ${token}`,
    'X-Correlation-ID': correlationId,
    'access-control-allow-headers': 'Retry-After',
  }

  const response = await fetch(fullUrl, { headers: headers })

  if (!response.ok) {
    if (response.status === 302) {
      const redirectedUrl = response.headers.get('Location')
      if (redirectedUrl) {
        const redirectedResponse = await fetch(redirectedUrl, { headers: headers })
        if (!redirectedResponse.ok) {
          throwFetcherrorFromResponse(redirectedResponse, correlationId)
        }

        return {
          body: await redirectedResponse.json(),
          headers: redirectedResponse.headers,
        }
      } else {
        return {
          body: await response.json(),
          etag: response.headers.get('ETag') || undefined,
          headers: response.headers,
        }
      }
    }

    if (response.status === 404 && fallback) return {}
    throwFetcherrorFromResponse(response, correlationId)
  }

  return {
    body: await response.json(),
    etag: response.headers.get('ETag') || undefined,
    headers: response.headers,
  }
}

export async function getContentWithBaseUrlAndToken(url: string, fallback = false): Promise<any> {
  const token = await acquireTokenSilent()
  const fullUrl = await generateFullUrl(url)
  const correlationId = uuidv4()

  const headers = { Authorization: `Bearer ${token}`, 'X-Correlation-ID': correlationId }

  const response = await fetch(fullUrl, { headers: headers })

  if (!response.ok) {
    if (response.status === 404 && fallback) return {}
    const error = new FetchError(
      'An error occurred while performing the request. Correlation ID: ' + correlationId,
    )
    error.status = response.status
    error.info = await response.json()
    error.correlationId = correlationId

    throw error
  }

  return {
    body: await response.text(),
    etag: response.headers.get('ETag') || undefined,
  }
}

export async function getWithBaseUrlTokenAndHeader(
  url: string,
  headers: Record<string, string>,
  fallback = false,
): Promise<any> {
  const token = await acquireTokenSilent()
  const correlationId = uuidv4()

  const reqHeaders = {
    ...(headers || {}),
    Authorization: `Bearer ${token}`,
    'X-Correlation-ID': correlationId,
  }

  const fullUrl = await generateFullUrl(url)

  const response = await fetch(fullUrl, { headers: reqHeaders })

  if (!response.ok) {
    if (response.status === 404 && fallback) return {}
    const error = new FetchError(
      'An error occurred while performing the request. Correlation ID: ' + correlationId,
    )
    error.status = response.status
    error.info = await response.json()
    error.correlationId = correlationId

    throw error
  }

  return {
    body: await response.json(),
    etag: response.headers.get('ETag') || undefined,
    headers: response.headers,
  }
}

export async function getFileWithBaseUrlAndToken(url: string): Promise<any> {
  const token = await acquireTokenSilent()
  const fullUrl = await generateFullUrl(url)
  const correlationId = uuidv4()
  const headers = { Authorization: `Bearer ${token}`, 'X-Correlation-ID': correlationId }

  try {
    const file = await axios(fullUrl, {
      method: 'GET',
      headers: headers,
      responseType: 'blob',
    })
    return file.data
  } catch (ex: any) {
    const error = new FetchError(
      'An error occurred while performing the request. Correlation ID: ' + correlationId,
    )
    error.status = ex.response.status
    error.info = ex.message
    error.correlationId = correlationId
    throw error
  }
}

export async function postWithBaseUrlAndToken(
  url: string,
  body: any,
  customHeaders?: any,
): Promise<any> {
  const token = await acquireTokenSilent()
  return requestBodyWithBaseUrl('POST', url, body, token, customHeaders)
}

export async function postTemplateWithBaseUrlAndToken(
  url: string,
  data: FormData,
  customHeaders?: any,
): Promise<any> {
  const token = await acquireTokenSilent()
  const fullUrl = await generateFullUrl(url)
  const correlationId = uuidv4()

  const headers = {
    ...(customHeaders || {}),
    'Content-Type': 'multipart/form-data',
    Authorization: `Bearer ${token}`,
    'X-Correlation-ID': correlationId,
  }
  const response = await axios(fullUrl, {
    method: 'POST',
    headers: headers,
    data: data,
  })

  if (response.status !== 201) {
    const error = new FetchError(
      'An error occurred while performing the request. Correlation ID: ' + correlationId,
    )
    error.status = response.status
    error.info = response.statusText
    error.correlationId = correlationId

    throw error
  }

  return response
}

export async function postFileWithBaseUrlAndToken(
  url: string,
  data: FormData,
  customHeaders?: any,
): Promise<any> {
  const token = await acquireTokenSilent()
  const fullUrl = await generateFullUrl(url)
  const correlationId = uuidv4()

  const headers = {
    ...(customHeaders || {}),
    'Content-type': 'multipart/form-data',
    Authorization: `Bearer ${token}`,
    'X-Correlation-ID': correlationId,
  }
  const response = await axios(fullUrl, {
    method: 'POST',
    headers: headers,
    data: data,
  })

  if (!(response.status === 200 || response.status === 202)) {
    const error = new FetchError(
      'An error occurred while performing the request. Correlation ID: ' + correlationId,
    )
    error.status = response.status
    error.info = response.statusText
    error.correlationId = correlationId

    throw error
  }

  return response
}

export async function putFileWithBaseUrlAndToken(
  url: string,
  data: FormData,
  customHeaders?: any,
): Promise<any> {
  const token = await acquireTokenSilent()
  const fullUrl = await generateFullUrl(url)
  const correlationId = uuidv4()

  const headers = {
    ...(customHeaders || {}),
    'Content-type': 'multipart/form-data',
    Authorization: `Bearer ${token}`,
    'X-Correlation-ID': correlationId,
  }
  const response = await axios(fullUrl, {
    method: 'PUT',
    headers: headers,
    data: data,
  })

  if (response.status !== 200) {
    const error = new FetchError(
      'An error occurred while performing the request. Correlation ID: ' + correlationId,
    )
    error.status = response.status
    error.info = response.statusText
    error.correlationId = correlationId

    throw error
  }

  return response
}

export async function putProjectFileWithBaseUrlAndToken(
  url: string,
  data: FormData,
  customHeaders?: any,
): Promise<any> {
  const token = await acquireTokenSilent()
  const fullUrl = await generateFullUrl(url)
  const correlationId = uuidv4()

  const headers = {
    ...(customHeaders || {}),
    'Content-type': 'multipart/form-data',
    Authorization: `Bearer ${token}`,
    'X-Correlation-ID': correlationId,
  }
  const response = await axios(fullUrl, {
    method: 'PUT',
    headers: headers,
    data: data,
  })

  if (response.status !== 200) {
    const error = new FetchError(
      'An error occurred while performing the request. Correlation ID: ' + correlationId,
    )
    error.status = response.status
    error.info = response.statusText
    error.correlationId = correlationId

    throw error
  }

  return response
}

export async function putWithBaseUrlAndToken(
  url: string,
  body: any,
  customHeaders?: any,
): Promise<any> {
  const token = await acquireTokenSilent()
  return requestBodyWithBaseUrl('PUT', url, body, token, customHeaders)
}

export async function patchWithBaseUrlAndToken(url: string, body: any): Promise<any> {
  const token = await acquireTokenSilent()
  return requestBodyWithBaseUrl('PATCH', url, body, token)
}

export async function deleteWithBaseUrlAndToken(url: string, customHeaders?: any): Promise<void> {
  const token = await acquireTokenSilent()
  const fullUrl = await generateFullUrl(url)
  const correlationId = uuidv4()

  const headers = {
    ...(customHeaders || {}),
    Authorization: `Bearer ${token}`,
    'X-Correlation-ID': correlationId,
  }
  const response = await fetch(fullUrl, {
    method: 'DELETE',
    headers: headers,
  })

  if (!response.ok) {
    const error = new FetchError(
      'An error occurred while performing the request. Correlation ID: ' + correlationId,
    )
    error.status = response.status
    error.info = await response.json()
    error.correlationId = correlationId

    throw error
  }
}

async function requestBodyWithBaseUrl(
  method: 'POST' | 'PUT' | 'PATCH',
  url: string,
  body: any,
  token: string,
  customHeaders?: any,
): Promise<any> {
  if (!token) return
  const fullUrl = await generateFullUrl(url)
  const correlationId = uuidv4()

  const headers = {
    ...(customHeaders || {}),
    'Content-type': 'application/json; charset=UTF-8',
    Authorization: `Bearer ${token}`,
    'X-Correlation-ID': correlationId,
  }

  const response = await fetch(fullUrl, {
    method: method,
    headers: headers,
    body: body ? JSON.stringify(body) : null,
  })

  if (!response.ok) {
    const error = new FetchError(
      'An error occurred while performing the request. Correlation ID: ' + correlationId,
    )
    error.status = response.status
    error.info = await response.json()
    error.correlationId = correlationId

    throw error
  }

  return response.json()
}

async function generateFullUrl(relativeUrl: string): Promise<string> {
  const BaseUrl = Config.api.baseUrl
  let tenantPath = ''

  if (!relativeUrl.toLowerCase().includes('/tenants')) {
    const tenantId = getCurrentTenantId()
    tenantPath = tenantId ? `/tenants/${tenantId}` : ''
  }

  const fullUrl = `${BaseUrl}${tenantPath}${relativeUrl}`
  return fullUrl
}

function throwFetcherrorFromResponse(response: Response, correlationId: string): never {
  const error = new FetchError(
    'An error occurred while performing the request. Correlation ID: ' + correlationId,
  )
  error.status = response.status
  error.info = response.json()
  error.correlationId = correlationId

  throw error
}
